import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './Download.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY);

const CheckoutForm = ({ onSuccess, calendarRef, framePrice, couponInfo, onCouponApply }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState(couponInfo?.couponCode || '');
  const [couponError, setCouponError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [hasCardInput, setHasCardInput] = useState(false);

  // クーポン適用後の金額を計算
  const originalAmount = 500 + framePrice;
  const finalAmount = couponInfo?.finalAmount ?? originalAmount;
  const isSubmitEnabled = Boolean(hasCardInput || couponCode.trim()) && !processing && !isValidating && Boolean(stripe);

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('クーポンコードを入力してください');
      return;
    }

    setIsValidating(true);
    setCouponError('');

    try {
      const baseURL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${baseURL}/validateCoupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          couponCode: couponCode.trim(),
          originalAmount,
        }),
      });

      const result = await response.json();

      if (result.valid) {
        onCouponApply({
          couponCode: couponCode.trim(),
          promotionCodeId: result.promotionCodeId,
          couponId: result.couponId,
          discountAmount: result.discountAmount,
          discountDisplay: result.discountDisplay,
          finalAmount: result.finalAmount,
        });
        setCouponError('');
      } else {
        setCouponError(result.message || 'クーポンが無効です');
        onCouponApply(null);
      }
    } catch (couponValidationError) {
      console.error('クーポン検証エラー:', couponValidationError);
      setCouponError('クーポンの検証中にエラーが発生しました');
      onCouponApply(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponError('');
    onCouponApply(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // メールアドレスを取得
    const email = event.target.email.value;
    console.log(email);
    // メールアドレスがなかったら、エラーを表示
    if (email === '') {
      setError('メールアドレスを入力してください。');
      setProcessing(false);
      return;
    }
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardElement),
    });

    if (error) {
      setError(error.message);
      setProcessing(false);
    } else {
      // ここでバックエンドAPIに支払い情報を送信
      try {
        const baseURL = process.env.REACT_APP_API_URL || '';
        // カレンダーのクローンを作成
        const clone = calendarRef.current.cloneNode(true);

        // 不要な要素を削除（画面用の矢印・ページネーション）
        const arrowsElement = clone.querySelector('.calendar_arrow');
        const paginationElement = clone.querySelector('.splide__pagination');
        if (arrowsElement) arrowsElement.remove();
        if (paginationElement) paginationElement.remove();

        // Splide のスライダー用ラッパーを取り除き、
        // 各月のプレビュー (.flex.preview) だけを縦に並べたシンプルな構造に変換
        const slideElements = clone.querySelectorAll('.splide__slide');
        if (slideElements && slideElements.length > 0) {
          const wrapper = document.createElement('div');
          slideElements.forEach((slide) => {
            const preview = slide.querySelector('.flex.preview');
            if (preview) {
              wrapper.appendChild(preview.cloneNode(true));
            }
          });
          clone.innerHTML = '';
          clone.appendChild(wrapper);
        }

        // クローン内のすべての画像を取得
        const images = clone.getElementsByTagName('img');

        // 画像のsrcをデータURLに変換
        for (let img of images) {
          if (img.src) {
            const response = await fetch(img.src);
            const blob = await response.blob();
            const reader = new FileReader();
            await new Promise((resolve) => {
              reader.onloadend = () => {
                img.src = reader.result;
                resolve();
              };
              reader.readAsDataURL(blob);
            });
          }
        }

        // PDF専用のスタイル（ページサイズに合わせた固定レイアウト）
        const pdfStyles = `
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: "M PLUS 1p", sans-serif;
            margin: 0;
            padding: 0;
          }
          .pdf-page {
            width: 148mm;
            height: 100mm;
            display: flex;
            justify-content: center;
            align-items: center;
            page-break-after: always;
            page-break-inside: avoid;
            overflow: hidden;
            position: relative;
          }
          .pdf-page:last-child {
            page-break-after: avoid;
          }
          .flex.preview {
            display: flex;
            width: 300px;
            height: 202.7px;
            justify-content: center;
            align-items: center;
            margin: 0;
            position: relative;
            overflow: hidden;
            transform: scale(1.6);
          }
          .month-image {
            width: 35%;
            margin-right: 10px;
            position: relative;
            z-index: 1;
          }
          .pre-image {
            width: 100%;
            height: auto;
            max-height: 100px;
            object-fit: contain;
          }
          .pre-calendar {
            width: 50%;
          }
          .pre-right {
            font-size: 10px;
            position: relative;
            z-index: 1;
          }
          .pre-h2 {
            font-size: 24px;
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
          }
          .pre-month-jp {
            display: flex;
            align-items: center;
            gap: 2px;
          }
          .calendar {
            display: grid;
            grid-template-columns: repeat(7, 18px);
            gap: 2px;
          }
          .calendar-cell {
            width: 18px;
            height: 16px;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0;
          }
          .calendar-cell.header {
            font-weight: bold;
            text-align: center;
            font-size: 6px;
          }
          .calendar-cell img {
            width: 7px;
            height: auto;
            max-height: 12px;
            object-fit: contain;
          }
          .calendar-cell.two-digits img {
            width: 5px;
            max-height: 10px;
          }
          .frame-image {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            z-index: 0;
          }
        `;

        // 各月のプレビューを .pdf-page でラップ
        const previewElements = clone.querySelectorAll('.flex.preview');
        let pdfPagesHtml = '';
        previewElements.forEach((preview) => {
          pdfPagesHtml += `<div class="pdf-page">${preview.outerHTML}</div>`;
        });

        // HTMLコンテンツを作成（1年分のカレンダーを含む）
        const htmlContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <meta charset="utf-8" />
              <link href="https://fonts.googleapis.com/css2?family=M+PLUS+1p&display=swap" rel="stylesheet">
              <style>${pdfStyles}</style>
            </head>
            <body>
              ${pdfPagesHtml}
            </body>
          </html>
        `;

        const response = await fetch(`${baseURL}/payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentMethodId: paymentMethod.id,
            email: email,
            htmlContent: htmlContent,
            amount: finalAmount,
            couponCode: couponInfo?.couponCode || null,
            promotionCodeId: couponInfo?.promotionCodeId || null,
          }),
        });
        
        if (response.ok) {
          onSuccess();
        } else {
          setError('決済に失敗しました。');
        }
      } catch (err) {
        setError('エラーが発生しました。');
      }
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <p className="download-mail">PDFを送信したいメールアドレス：<br />
        <input type="text" name="email" placeholder="coromi@seeda.jp" className="download-mail-input" />
      </p>
      <div className="card-element-container">
        {/* CardElementに郵便番号収集を無効にするオプションを追加 */}
        <CardElement 
          options={{ 
            hidePostalCode: true,
            style: { 
              base: { 
                fontFamily: '"M PLUS 1p", sans-serif',
              }
            }
          }}
          onChange={(event) => setHasCardInput(!event.empty)}
        />
      </div>
      <div className="download-coupon-section">
        <p className="download-coupon-label">クーポンコード（任意）</p>
        <div className="download-coupon-input-wrapper">
          <input
            type="text"
            className="download-coupon-input"
            placeholder="クーポンコードを入力"
            value={couponCode}
            onChange={(event) => setCouponCode(event.target.value)}
            disabled={!!couponInfo || isValidating}
          />
          {couponInfo ? (
            <button
              type="button"
              className="download-coupon-button download-coupon-remove"
              onClick={handleRemoveCoupon}
            >
              取消
            </button>
          ) : (
            <button
              type="button"
              className="download-coupon-button"
              onClick={handleValidateCoupon}
              disabled={isValidating}
            >
              {isValidating ? '確認中...' : '適用'}
            </button>
          )}
        </div>
        {couponError && <p className="download-coupon-error">{couponError}</p>}
        {couponInfo && <p className="download-coupon-success">✓ {couponInfo.discountDisplay}のクーポンが適用されました</p>}
      </div>
      
      <p className="termlead"><a href="https://seeda.fun/terms/coromi_terms/" className="otherlink" target="_blank" rel="noreferrer">利用規約</a>に同意してPDFを取得する</p>
      <button
        type="submit"
        className={`download-button${isSubmitEnabled ? ' is-active' : ''}`}
        disabled={!isSubmitEnabled}
      >
        {processing ? '処理中...' : 'メールに送信する'}
      </button>
      {error && <div className="error-message">{error}</div>}
    </form>
  );
};

function Download({ calendarRef, framePrice, couponInfo, onCouponApply }) {
  const [, setIsDownloaded] = useState(false);
  const options = {
    fonts: [{
      cssSrc: "https://fonts.googleapis.com/css2?family=M+PLUS+1p&display=swap"
    }]
  }

  const handlePaymentSuccess = () => {
    setIsDownloaded(true);
    // ここでダウンロード処理を実行
  };

  return (
    <div className="download">
      <p className="lead">アメリカン・エキスプレス、中国銀聯 (CUP)、ディスカバーとダイナースクラブ、eftpos オーストラリア、JCB、Mastercard、Visaのクレジットカードが利用可能です。</p>

      <Elements stripe={stripePromise} options={options}>
        <CheckoutForm onSuccess={handlePaymentSuccess} calendarRef={calendarRef} framePrice={framePrice} couponInfo={couponInfo} onCouponApply={onCouponApply} />
      </Elements>
    </div>
  );
}

export default Download;
